import _ from 'lodash';
import { inject } from '@/inversify.inject';
import { injectable } from '@/inversify.injectable';
import { KEY } from '@/inversify.keys';
import HelperService from '../common/services/helper.service';
import StoreFacade from '../common/services/store-facade';
import RatesService from '../rates/rates.service';
import { TriggerScheduleModel } from './models/trigger-schedule.model';
import RMSApiService from './rms-api.service';
import RMSStore from './rms.store';
import DocumentFiltersService from '../document-filters/document-filters.service';
import MealTypesService, { MealTypesServiceS } from '../meal-types/meal-types.service';
import ANY_MEAL_TYPE from '../meal-types/constants/any-meal-type.constant';

interface RMSPublicInterface {

}

@injectable()
export default class RMSService implements RMSPublicInterface {
    @inject(KEY.RMSApiService)
    private rmsApiService!: RMSApiService;

    @inject(KEY.StoreFacade)
    private storeFacade!: StoreFacade;

    @inject(KEY.HelperService)
    private helperService!: HelperService;

    @inject(KEY.RatesService)
    private ratesService!: RatesService;

    @inject(KEY.DocumentFiltersService)
    private documentFiltersService!: DocumentFiltersService;

    @inject(MealTypesServiceS)
    private mealTypesService!: MealTypesService;

    private storeState: RMSStore = this.storeFacade.getState('RMSStore');

    constructor() {
        this.storeFacade.watch(() => this.storeState.triggerSchedules.filters.compsetId, () => {
            this.storeState.triggerSchedules.loading.reset();
        });
    }

    get triggerSchedules() {
        this.helperService.dynamicLoading(
            this.storeState.triggerSchedules.loading,
            this.loadTriggerSchedules.bind(this),
        );
        return this.storeState.triggerSchedules.data;
    }

    get triggerSchedulesFilters() {
        return this.storeState.triggerSchedules.filters;
    }

    get isTriggerSchedulesLoading() {
        if (!this.triggerSchedules) return true;
        return this.storeState.triggerSchedules.loading.isLoading();
    }

    assignRatesFilters(data: TriggerScheduleModel) {
        const { mealTypeId } = data.filters;

        Object.assign(
            this.ratesService.settings,
            _.pick(data.filters, ['numberOfGuests', 'roomTypeId', 'provider']),
        );

        // NOTE: BE returns mealTypeId as string so we should convert it to ID
        this.ratesService.settings.mealTypeId = this.mealTypesService.getIdByName(String(mealTypeId))?.id ?? ANY_MEAL_TYPE.id;

        Object.assign(
            this.documentFiltersService.settings,
            _.pick(data.filters, ['los', 'pos']),
        );

        this.documentFiltersService.compsetId = this.storeState.triggerSchedules.filters.compsetId;
    }

    private async loadTriggerSchedules() {
        const { compsetId } = this.storeState.triggerSchedules.filters;
        if (!compsetId) return false;

        this.storeState.triggerSchedules.data = await this.rmsApiService
            .getTriggerSchedules(compsetId);

        return true;
    }
}
